import type { TalkingPoint } from "@acto/genui-components-schema";
import { addDaysUtc, startOfDayUtc, todayUtc, toIsoDate } from "@acto/web-common/dates";
import type { PeerPotentialsItem } from "@acto/taskpane/field-sales-2/data-access/usePeerPotentials";
import { entityListRows, resolvable } from "./entityListRows";

export interface HideableProduct {
  /** Identity for selection. A row covering several catalog ids is still picked once. */
  id: string;
  name: string;
  valueLabel?: string;
  productIds: string[];
}

export type DurationChoice = { kind: "once" } | { kind: "forever" } | { kind: "until"; day: Date | null };

export type SettledDuration = { kind: "once" } | { kind: "forever" } | { kind: "until"; day: Date };

export const settledDuration = (choice: DurationChoice): SettledDuration | null => {
  if (choice.kind !== "until") return choice;
  return choice.day === null ? null : { kind: "until", day: choice.day };
};

/** `once` hides for the rest of today only. */
export const validToForDuration = (choice: SettledDuration, today: Date = todayUtc()): string | null => {
  switch (choice.kind) {
    case "once":
      return toIsoDate(today);
    case "forever":
      return null;
    case "until":
      return toIsoDate(startOfDayUtc(choice.day));
  }
};

export const earliestCustomDay = (today: Date = todayUtc()): Date => addDaysUtc(today, 1);

type EntityListCard = Extract<TalkingPoint, { type: "entityList" }>;

/** Exclusions are scoped to this one signal: a card naming products in its rows does not make them hideable. */
const RECOMMENDATIONS_CARD_KEY = "recommended_products";

const isRecommendationsCard = (point: TalkingPoint): point is EntityListCard =>
  point.type === "entityList" && point.cardKey === RECOMMENDATIONS_CARD_KEY;

export const hideableProductsOf = (point: TalkingPoint): HideableProduct[] => {
  if (!isRecommendationsCard(point)) return [];
  return entityListRows(point).flatMap(({ productIds, title, value }) => {
    if (productIds.length === 0) return [];
    return [{ id: productIds.join(","), name: title, productIds, valueLabel: value?.text }];
  });
};

export const hideableProductsOfPeerRows = (rows: PeerPotentialsItem[]): HideableProduct[] =>
  rows.flatMap(({ productId, productName, formattedValue }) =>
    resolvable([productId]).map((id) => ({
      id,
      name: productName ?? id,
      productIds: [id],
      valueLabel: formattedValue ?? undefined,
    })),
  );

export const productNamesById = (products: HideableProduct[]): Map<string, string> =>
  new Map(products.flatMap((product) => product.productIds.map((id) => [id, product.name] as const)));

export const isProductHideable = (point: TalkingPoint): boolean => hideableProductsOf(point).length > 0;
